/// <reference types="google.maps" />
"use client";

import { useEffect, useRef } from "react";
import { Search } from "lucide-react";
import { usePlacesWidget } from "react-google-autocomplete";
import { Input } from "../Input";
import { InputGroup } from "../InputGroup";
import { cn } from "../../../lib/utils";
import type { AddressData, AddressInputProps, AddressInputValue } from "./AddressInput.types";

/**
 * Formats any shape of address value into a human-readable full display string.
 */
export function formatAddressForDisplay(value?: AddressInputValue): string {
  if (!value) return "";
  if (typeof value === "string") return value;

  if (value.formatted_address) {
    return value.formatted_address;
  }

  const parts = [
    value.street_address || value.address_line_1,
    value.city,
    value.state_or_region,
    value.postal_code,
    value.country,
  ].filter(Boolean);

  return parts.join(", ");
}

export function AddressInput({
  value,
  onChange,
  onSelectAddress,
  apiKey,
  countries = ["gb"],
  label = "Address",
  error,
  errors,
  required = false,
  className,
  inputClassName,
  placeholder = "Enter address or search with Google...",
  disabled = false,
  autocomplete = true,
}: AddressInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isInteractingRef = useRef(false);
  const lastEmittedRef = useRef<string>("");

  const initialDisplay = formatAddressForDisplay(value);

  const { ref: placesRef } = usePlacesWidget<HTMLInputElement>({
    apiKey,
    onPlaceSelected: (place) => {
      const components = place.address_components || [];
      const parsed = parseAddressComponents(components);
      const fullFormattedAddress =
        place.formatted_address ||
        (parsed.street_address
          ? [parsed.street_address, parsed.city, parsed.state_or_region, parsed.postal_code, parsed.country]
              .filter(Boolean)
              .join(", ")
          : "");

      let lat: number | null = null;
      let lng: number | null = null;
      if (place.geometry?.location) {
        if (typeof place.geometry.location.lat === "function") {
          lat = place.geometry.location.lat();
        } else if (typeof (place.geometry.location as any).lat === "number") {
          lat = (place.geometry.location as any).lat;
        }
        if (typeof place.geometry.location.lng === "function") {
          lng = place.geometry.location.lng();
        } else if (typeof (place.geometry.location as any).lng === "number") {
          lng = (place.geometry.location as any).lng;
        }
      }

      const newAddress: AddressData = {
        ...parsed,
        is_manual_entry: false,
        place_id: place.place_id,
        latitude: lat,
        longitude: lng,
        formatted_address: fullFormattedAddress,
      };

      lastEmittedRef.current = fullFormattedAddress;
      if (inputRef.current) {
        inputRef.current.value = fullFormattedAddress;
      }

      onChange?.(newAddress);
      onSelectAddress?.(newAddress);

      // Async fallback if geometry was omitted from autocomplete payload
      if ((lat === null || lng === null) && (window as any).google?.maps?.Geocoder) {
        try {
          const geocoder = new (window as any).google.maps.Geocoder();
          const req: any = place.place_id ? { placeId: place.place_id } : { address: fullFormattedAddress };
          geocoder.geocode(req, (results: any, status: string) => {
            if (status === "OK" && results?.[0]?.geometry?.location) {
              const gLoc = results[0].geometry.location;
              const gLat = typeof gLoc.lat === "function" ? gLoc.lat() : Number(gLoc.lat);
              const gLng = typeof gLoc.lng === "function" ? gLoc.lng() : Number(gLoc.lng);
              const updatedAddress: AddressData = {
                ...newAddress,
                latitude: gLat,
                longitude: gLng,
              };
              onChange?.(updatedAddress);
              onSelectAddress?.(updatedAddress);
            }
          });
        } catch (e) {
          console.warn("Geocoder fallback in AddressInput failed:", e);
        }
      }
    },
    options: {
      types: ["address"],
      componentRestrictions: { country: countries },
      fields: ["address_components", "formatted_address", "geometry", "place_id"],
    },
  });

  const formattedDisplay = formatAddressForDisplay(value);

  // Sync external value when not actively typing/interacting
  useEffect(() => {
    if (isInteractingRef.current) return;
    if (formattedDisplay && inputRef.current && inputRef.current.value !== formattedDisplay && lastEmittedRef.current !== formattedDisplay) {
      inputRef.current.value = formattedDisplay;
    }
  }, [formattedDisplay]);

  const parseAddressComponents = (components: google.maps.GeocoderAddressComponent[]) => {
    let streetNumber = "";
    let route = "";
    let city = "";
    let postalCode = "";
    let countryCode = "";
    let stateOrRegion = "";

    components.forEach((component) => {
      const types = component.types;
      if (types.includes("street_number")) streetNumber = component.long_name;
      if (types.includes("route")) route = component.long_name;
      if (types.includes("locality") || types.includes("postal_town") || types.includes("sublocality_level_1")) {
        city = component.long_name;
      }
      if (types.includes("postal_code")) postalCode = component.long_name;
      if (types.includes("country")) countryCode = component.short_name.toUpperCase();
      if (types.includes("administrative_area_level_1") || types.includes("administrative_area_level_2")) {
        if (!stateOrRegion || types.includes("administrative_area_level_1")) {
          stateOrRegion = component.long_name;
        }
      }
    });

    const streetAddress = [streetNumber, route].filter(Boolean).join(" ");

    return {
      address_line_1: streetAddress,
      street_address: streetAddress,
      street_number: streetNumber,
      route: route,
      city,
      postal_code: postalCode,
      country: countryCode,
      state_or_region: stateOrRegion,
    };
  };

  const handleAutocompleteInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const manualVal = e.target.value;
    lastEmittedRef.current = manualVal;
    onChange?.({
      address_line_1: manualVal,
      street_address: manualVal,
      city: typeof value === "object" && value ? value.city || "" : "",
      postal_code: typeof value === "object" && value ? value.postal_code || "" : "",
      country: typeof value === "object" && value ? value.country || "" : "",
      state_or_region: typeof value === "object" && value ? value.state_or_region || "" : "",
      is_manual_entry: true,
      formatted_address: manualVal,
    });
  };

  // Extract structured address parts for line-by-line mode
  const streetValue =
    typeof value === "object" && value
      ? value.street_address || value.address_line_1 || ""
      : typeof value === "string"
      ? value
      : "";
  const cityValue = typeof value === "object" && value ? value.city || "" : "";
  const stateValue = typeof value === "object" && value ? value.state_or_region || "" : "";
  const postalCodeValue = typeof value === "object" && value ? value.postal_code || "" : "";
  const countryValue =
    typeof value === "object" && value && value.country !== undefined && value.country !== ""
      ? value.country
      : countries?.[0]?.toUpperCase() || "GB";
  const latValue = typeof value === "object" && value ? value.latitude ?? null : null;
  const lngValue = typeof value === "object" && value ? value.longitude ?? null : null;

  const handleLineChange = (
    field: "street_address" | "city" | "state_or_region" | "postal_code" | "country",
    newValue: string
  ) => {
    const updatedStreet = field === "street_address" ? newValue : streetValue;
    const updatedCity = field === "city" ? newValue : cityValue;
    const updatedState = field === "state_or_region" ? newValue : stateValue;
    const updatedPostal = field === "postal_code" ? newValue : postalCodeValue;
    const updatedCountry = field === "country" ? newValue : countryValue;

    const formatted = [updatedStreet, updatedCity, updatedState, updatedPostal, updatedCountry]
      .filter(Boolean)
      .join(", ");

    onChange?.({
      street_address: updatedStreet,
      address_line_1: updatedStreet,
      city: updatedCity,
      state_or_region: updatedState,
      postal_code: updatedPostal,
      country: updatedCountry,
      is_manual_entry: true,
      latitude: latValue,
      longitude: lngValue,
      formatted_address: formatted,
    });
  };

  if (!autocomplete) {
    return (
      <div className={cn("flex w-full flex-col gap-4", className)}>
        {/* Street Address */}
        <InputGroup label="Street Address" required={required} error={errors?.street_address || error}>
          <Input
            placeholder={placeholder || "Street address, house/flat number, P.O. box"}
            value={streetValue}
            disabled={disabled}
            className={cn("bg-white border-[1.5px] border-gray-100 rounded-xl focus:border-secondary transition-all duration-200 h-12", inputClassName)}
            onChange={(e) => handleLineChange("street_address", e.target.value)}
          />
        </InputGroup>

        {/* City & State / Region */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputGroup label="City / Town" required={required} error={errors?.city}>
            <Input
              placeholder="e.g. London"
              value={cityValue}
              disabled={disabled}
              className={cn("bg-white border-[1.5px] border-gray-100 rounded-xl focus:border-secondary transition-all duration-200 h-12", inputClassName)}
              onChange={(e) => handleLineChange("city", e.target.value)}
            />
          </InputGroup>
          <InputGroup label="State / Region / County" required={required} error={errors?.state_or_region}>
            <Input
              placeholder="e.g. Greater London"
              value={stateValue}
              disabled={disabled}
              className={cn("bg-white border-[1.5px] border-gray-100 rounded-xl focus:border-secondary transition-all duration-200 h-12", inputClassName)}
              onChange={(e) => handleLineChange("state_or_region", e.target.value)}
            />
          </InputGroup>
        </div>

        {/* Postal Code & Country */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputGroup label="Postal / ZIP Code" required={required} error={errors?.postal_code}>
            <Input
              placeholder="e.g. SW1A 1AA"
              value={postalCodeValue}
              disabled={disabled}
              className={cn("bg-white border-[1.5px] border-gray-100 rounded-xl focus:border-secondary transition-all duration-200 h-12", inputClassName)}
              onChange={(e) => handleLineChange("postal_code", e.target.value)}
            />
          </InputGroup>
          <InputGroup label="Country" required={required} error={errors?.country}>
            <Input
              placeholder="e.g. GB or United Kingdom"
              value={countryValue}
              disabled={disabled}
              className={cn("bg-white border-[1.5px] border-gray-100 rounded-xl focus:border-secondary transition-all duration-200 h-12", inputClassName)}
              onChange={(e) => handleLineChange("country", e.target.value)}
            />
          </InputGroup>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex w-full flex-col", className)}>
      <InputGroup label={label} required={required} error={error}>
        <div className="relative group">
          <Input
            ref={(node: HTMLInputElement | null) => {
              inputRef.current = node;
              if (placesRef) {
                (placesRef as any).current = node;
              }
            }}
            placeholder={placeholder}
            disabled={disabled}
            leadingIcon={<Search className="text-muted-foreground" />}
            className={cn("bg-accent/5 hover:bg-accent/10 border-accent/20 border-border transition-all duration-300", inputClassName)}
            defaultValue={initialDisplay}
            onFocus={() => {
              isInteractingRef.current = true;
            }}
            onBlur={() => {
              isInteractingRef.current = false;
            }}
            onChange={handleAutocompleteInputChange}
          />
        </div>
      </InputGroup>
    </div>
  );
}

